import { type IntlShape, createIntl } from 'react-intl-next';
import { ref, proxy } from 'valtio';

import { getDefaultLocale } from '@canvas/constants/localization/localization-types';

import { getDefaultMessages } from './default-messages';
import { type MessageDescriptor } from './intl-types';

export type IntlStatus = 'uninitialised' | 'fetching' | 'succeeded' | 'failed';

type IntlState = {
  status: IntlStatus;
  intl: IntlShape;
};

type ExternalIntlState = {
  intl: IntlShape;
};

const createFallbackIntl = () =>
  createIntl({
    locale: getDefaultLocale(),
    onError: (_err: unknown) => {},
  });

/**
 * Global state that can be used to access the intl object imperatively
 * (e.g. in a non-react context).
 * NOTE: Calls to i18n.intl.formatMessage MUST occur at runtime after the
 * app has mounted, otherwise the intl object will not be initialised
 * with correct messages. Eg, you must not define a constant in global context like
 * const myMessage = i18n.intl.formatMessage(...);
 */
export const i18n = proxy<IntlState>({
  status: 'uninitialised',
  // A reference to the intl object
  // https://formatjs.io/docs/react-intl/api/#the-intl-object
  // Note: We silence the console.errors() from missing translations as these are disruptive to
  // local development
  intl: ref(
    createIntl({
      locale: getDefaultLocale(),
      messages: getDefaultMessages(),
      onError: (_err: unknown) => {},
    }),
  ),
});

// External messages are used to load the messages for atlaskit dependencies like the editor,
// smart cards
export const externalMessages: Record<string, { intl: IntlShape }> = {};

export function getOrCreateExternalIntlState(key: string): ExternalIntlState {
  if (!externalMessages[key]) {
    externalMessages[key] = proxy<ExternalIntlState>({
      intl: ref(createFallbackIntl()),
    });
  }

  return externalMessages[key];
}

export function getIntlStateForMessage(
  message?: Pick<MessageDescriptor, 'externalMessagesKey'>,
): Pick<IntlState, 'intl'> {
  return message?.externalMessagesKey
    ? getOrCreateExternalIntlState(message.externalMessagesKey)
    : i18n;
}

export function getIntlForMessage(
  message?: Pick<MessageDescriptor, 'externalMessagesKey'>,
) {
  return getIntlStateForMessage(message).intl;
}
